/**
 * Per-route <head> for client-side navigation and for Googlebot (which runs JS).
 * LLM/social crawlers that don't run JS are served the same tags baked into the
 * HTML at build time by scripts/prerender.ts — both read src/lib/seo/config.ts,
 * so the runtime head and the prerendered head can't drift.
 *
 * <Seo> takes an explicit SeoMeta (dynamic pages: /property/:id, /rooms/:city).
 * <RouteSeo> is mounted once and covers every static marketing route from its
 * pathname — no per-page wiring.
 */

import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { baseLanguageFor } from "@/lib/i18n/languages";
import { SITE, staticRouteMeta, type SeoMeta } from "@/lib/seo/config";

export function Seo({ meta }: { meta: SeoMeta }) {
  return (
    <Helmet>
      <html lang={meta.lang} />
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <link rel="canonical" href={meta.canonical} />
      {meta.robots && <meta name="robots" content={meta.robots} />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={meta.canonical} />
      <meta property="og:image" content={meta.image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={SITE.twitter} />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={meta.image} />

      {(meta.jsonLd ?? []).map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}

/**
 * Mounted once inside LanguageProvider (for useLanguage) and BrowserRouter (for
 * useLocation). Emits meta for any known static route; dynamic pages render
 * their own <Seo> with DB-derived meta, so an unknown pathname here is a no-op.
 */
export function RouteSeo() {
  const { pathname } = useLocation();
  const { language } = useLanguage();
  const meta = staticRouteMeta(pathname, baseLanguageFor(language));
  return meta ? <Seo meta={meta} /> : null;
}
